import { CommonModule } from "@angular/common";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { CookieService } from "ngx-cookie-service";
import { NgxSpinnerModule } from "ngx-spinner";
import { of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";

import { environment } from '../environments/environment';

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { MockModule } from "./mock/mock.module";
import { AuthGuard } from "./shared";
import { SuperOpsGuard } from "./shared";
import { CsvExporterModule } from "./shared/modules/csv-exporter/module";
import { FlashMessagesModule } from "./shared/modules/flash-messages/module";
import { AuthenticationService } from "./shared/services/authentication.service";
import { ModalsModule } from "./shared/modules/modals/modals.module";
import { PaginationModule } from "./shared/modules/pagination/pagination.module";
import { InstanceService } from "./shared/services/instance.service";
import { LookAndFeelService } from "./shared/services/look-and-feel.service";
import { ApiService } from "./shared/services/api.service";

// AoT requires an exported function for factories
export function createTranslateLoader(http: HttpClient) {
    // for development
    // return new TranslateHttpLoader(http, '/start-angular/SB-Admin-BS4-Angular-5/master/dist/assets/i18n/', '.json');
    return new TranslateHttpLoader(http, "./assets/i18n/", ".json");
}

@NgModule({
    imports: [
        CommonModule,
        BrowserModule,
        FormsModule,
        BrowserAnimationsModule,
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        FlashMessagesModule,
        CsvExporterModule,
        AppRoutingModule,
        NgxSpinnerModule,
        ModalsModule,
        PaginationModule,
        MockModule
    ],
    declarations: [AppComponent],
    providers: [
        AuthGuard,
        SuperOpsGuard,
        AuthenticationService,
        CookieService,
        InstanceService,
        LookAndFeelService,
        {
            provide: APP_INITIALIZER,
            useFactory: initAppCenterAsssetsCookies,
            multi: true,
            deps: [CookieService, InstanceService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initTranslationsService,
            multi: true,
            deps: [ApiService, CookieService, TranslateService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            multi: true,
            deps: [CookieService, InstanceService, LookAndFeelService]
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initLookAndFeel,
            multi: true,
            deps: [CookieService, HttpClient, LookAndFeelService, InstanceService]
        }
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}

function initTranslationsService(apiService: ApiService, cookieService: CookieService, translate: TranslateService ){
    return () => {

        translate.addLangs(['en_US', 'zh_CN', 'es']);
        // translate.setDefaultLang('en_US');
        const browserLang = translate.getBrowserLang();
        translate.use(browserLang.match(/en_US|zh_CN|es/) ? browserLang: 'en_US');

        if (!cookieService.check("app-center-sessionId"))
            return;
        return apiService.get("locale.get", {}).pipe(
            map((data) => {
                if (data["cmd"] !== undefined && data["cmd"]["success"]) {
                    return data["cmd"]["params"];
                }
                return data["cmd"];
            }),
            tap( result => {
            if(result){
                cookieService.set(
                    'app-center-lang', result.locale,
                    9999, '/',  window.location.hostname,
                    true, "Strict"
                );

                if(translate.currentLang !== result.locale) translate.use(result.locale);
            }})
        );
    }
}


export function initAppCenterAsssetsCookies(cookieService: CookieService, instanceService: InstanceService){
    return() => {
        let prefix = cookieService.get("app-center-instance");
        if(prefix && instanceService.instance){
            cookieService.set('app-center-instances-url', instanceService.appCenterInstanceUrl, 400,"/",window.location.hostname,true);
            cookieService.set('app-center-current-instance', instanceService.appCenterCurrentInstance, 400,"/",window.location.hostname,true);
            cookieService.set('app-center-endpoint', instanceService.appCenterEndpoint, 400,"/",window.location.hostname,true);
            cookieService.set('app-center-url', instanceService.appCenterUrl, 400,"/",window.location.hostname,true);
        }
    }
}

export function initConfig(cookieService: CookieService, instanceService: InstanceService, lookAndFeelService: LookAndFeelService){
    return() => {
        let instance = cookieService.get("app-center-instance");
        if(instance && environment.instances[instance]){
            const platformInstances = environment.instances;
            localStorage.setItem("app-center-config-object", JSON.stringify(platformInstances));
            (typeof platformInstances === 'object') ?
                localStorage.setItem("app-center-config", JSON.stringify(platformInstances[instance])):
                localStorage.setItem("app-center-config", JSON.stringify(JSON.parse(JSON.stringify(platformInstances)).find((item: any) => item.key === instance)));
        }
    }
}

export function initLookAndFeel(cookieService: CookieService, http: HttpClient, lookAndFeelService: LookAndFeelService, instanceService){
    return() => {
        if(instanceService.instance.lnfUrl && instanceService.instance.lnfUrl !== ""){
                http
                    .get(decodeURI(instanceService.instance.lnfUrl.replace(/['"]+/g, "")))
                    .pipe(
                        catchError( err=> { console.log(err); return of(null)})
                    )
                    .subscribe(resp => {
                        if(resp && resp?.landf) {
                            localStorage.setItem("app-center-lnf", JSON.stringify(resp?.landf));
                            lookAndFeelService.setLookAndFeel(resp?.landf)
                        }
                    });
        }
    }
}
